import { memo } from 'react';
import { Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';

import { SVGIconType } from '../../../../constants/svgIcon';
import useLocale from '../../../../hooks/useLocale';
import { AlbumPartition, Space } from '../../../../models/SpaceStore';
import { Regions } from '../../../../models/SystemStore/SystemStore';
import { WebBoosterType } from '../../../../models/UserStore';
import {
  AlbumCell,
  AlbumCellProps,
  ClubHeader,
  DefaultBackButton,
  FolderBreadcrumbRow,
  FolderBreadcrumbVariant,
  JoyajoyImage,
  LoadMoreSpinner,
  ScrollArrows,
  SvgIcon,
} from '../../../Molecules';

import styles from './ClubTemplate.module.css';

export type ClubTemplateProps = {
  space: Space;
  webBoosterType: WebBoosterType | undefined;
  breadcrumbs: AlbumPartition[];
  hasRecentAlbum: boolean;
  albumData: AlbumCellProps[];
  showAppBarTitle: boolean;
  translatedRegions: Regions | undefined;
  isLoading: boolean;
  hasCursor: boolean;
  onClickAIRevision: () => void;
  onClickRecentAlbum: () => void;
  onClickBreadcrumb: () => void;
  loadMoreItems: () => void;
};

export const ClubTemplate = memo((props: ClubTemplateProps) => {
  const { t } = useTranslation();

  const {
    space,
    albumData,
    translatedRegions,
    showAppBarTitle,
    webBoosterType,
    breadcrumbs,
    hasRecentAlbum,
    isLoading,
    hasCursor,
    // onClickAIRevision,
    onClickRecentAlbum,
    onClickBreadcrumb,
    loadMoreItems,
  } = props;

  const l = useLocale();

  const classroomInfo = l(space.i18nClassroomInfo);
  const spaceName = classroomInfo.name;
  const spaceIconSource = space?.icon?.publicUrls?.small ?? '';
  // const spaceI18nInfo = l(space.i18nClassroomInfo);

  return (
    <div className={styles.clubTemplate}>
      <div className={styles.centeredContainer}>
        <AnimatePresence>
          {showAppBarTitle && (
            <motion.div
              className={styles.fixedClubBar}
              initial={{ y: -100 }}
              animate={{ y: 0 }}
              exit={{ y: -100 }}
              transition={{
                duration: 0.3,
                ease: 'easeOut',
              }}
            >
              <DefaultBackButton />
              <div className={styles.imageWrapper}>
                <JoyajoyImage
                  resource={{ uri: spaceIconSource }}
                  className={styles.clubIcon}
                />
              </div>
              <div className={styles.spaceName}>{spaceName}</div>
            </motion.div>
          )}
        </AnimatePresence>
        {/* {showAppBarTitle && (
          <AppBar
            withBack={false}
            title={spaceName}
            leading={
              <div className={styles.imageWrapper}>
                <JoyajoyImage
                  resource={{ uri: spaceIconSource }}
                  className={styles.clubIcon}
                />
              </div>
            }
          />
        )} */}
        <ScrollArrows />

        {/* <div className={styles.titleImageContainer}>
          <DefaultBackButton />
          <div className={styles.imageWrapper}>
            <JoyajoyImage
              resource={{ uri: spaceIconSource }}
              className={styles.image}
            />
          </div>
          <div className={styles.titleContainer}>
            <div className={styles.spaceName}>{spaceName}</div>
            <div className={styles.subtitle}>
              {spaceI18nInfo?.shortDescription}
            </div>
          </div>
        </div> */}

        <div className={styles.backButtonContainer}>
          <DefaultBackButton />
        </div>

        <ClubHeader
          space={space}
          translatedRegions={translatedRegions}
          // showIconRow={false}
        />

        <div className={styles.folderBreadcrumbRow}>
          <div>{t('Path: ')}</div>
          <FolderBreadcrumbRow
            webBoosterType={webBoosterType}
            breadcrumbs={breadcrumbs}
            variant={FolderBreadcrumbVariant.Black}
            onClickBreadcrumb={onClickBreadcrumb}
          />
        </div>

        {isLoading ? (
          <div
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '1rem',
            }}
          >
            <Spinner
              animation="border"
              role="status"
              style={{ color: '#05656B' }}
            >
              <span className="visually-hidden">{t('Loading...')}</span>
            </Spinner>
          </div>
        ) : albumData.length === 0 ? (
          <>
            <div className={styles.myJourneys}>
              {/* <div className={'sectionTitle'}>{t('My Journeys')}</div> */}

              <div className={styles.emptyContainer}>
                <SvgIcon
                  name={SVGIconType.PracticeTick}
                  className={styles.emptyAlbumIcon}
                  width={80}
                  height={80}
                  color={'#333333'}
                />
                <div className={styles.emptyAlbumText}>
                  {t('No Memory Practice Yet')}
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
            {hasRecentAlbum ? (
              <div className={styles.whiteBarContainer}>
                <div className={styles.whiteBar} onClick={onClickRecentAlbum}>
                  <SvgIcon
                    name={SVGIconType.RecentArrow}
                    width={28}
                    height={28}
                    color={'#333'}
                  />
                  <div className={styles.whiteBarText}>
                    {t('Recently Played')}
                  </div>
                  <div className={styles.whiteBarArrow}>
                    <SvgIcon
                      name={SVGIconType.Arrow}
                      width={20}
                      height={20}
                      color="#808080"
                    />
                  </div>
                </div>
              </div>
            ) : null}

            <div className={styles.myJourneys}>
              {/* <div className={'sectionTitle'}>{t('My Journeys')}</div> */}

              <div className={styles.albumList}>
                {albumData.map((item, index) => (
                  <AlbumCell key={index} {...item} />
                ))}
              </div>
            </div>

            {hasCursor && (
              <LoadMoreSpinner
                hasCursor={hasCursor}
                loadMoreItems={loadMoreItems}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
});
